import { useCallback, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { authService } from '@/services/auth.service';
import { activateProactiveAccessTokenRefresh } from '@/services/api';
import { setCredentials } from '@/redux/slices/authSlice';
import {
  clearPosShiftStoreId,
  isPosShiftStoreBindingStale,
  resolveActiveStoreId,
  resolvePosShiftStoreToPersist,
  setPosShiftStoreId,
  setPosShiftStoreTenantId,
  usePosShiftStoreId,
} from '@/utils/posShift';
import type { QueryClient } from '@tanstack/react-query';
import type { RootState } from '@/redux';

/**
 * Mọi query bị scope theo store — phải làm mới HẾT khi đổi chi nhánh.
 *
 * TCR-980: `pos-warehouse-stocks` từng bị bỏ sót ở đây. Modal "Chọn kho xuất
 * hàng" trên POS không truyền `storeId` (store đến từ interceptor) nên cache
 * của nó không tự hết hạn theo chi nhánh — thu ngân đổi chi nhánh mà danh sách
 * kho vẫn là của chi nhánh cũ. Gom vào một hàm để lần thêm query scope-store
 * tiếp theo chỉ phải sửa một chỗ.
 */
/**
 * TCR-1694: `pos-browse` (lưới sản phẩm), `pos-promotions` và `pos-variant-serials`
 * từng bị bỏ sót y như `pos-warehouse-stocks` trước đó. `invalidateQueries` khớp theo
 * PHẦN TỬ đầu của key, không khớp theo tiền tố chuỗi — `['pos']` KHÔNG phủ
 * `['pos-browse']`. Hậu quả quan sát được: sau khi đổi/sửa chi nhánh, ô tìm khách hàng
 * hồi phục nhưng lưới sản phẩm kẹt vĩnh viễn ở "Không tải được danh sách sản phẩm,
 * thử lại sau" vì query hỏng không bao giờ được refetch.
 *
 * Danh sách để rời ở đây (thay vì rải lời gọi) để lần thêm query scope-store tiếp
 * theo chỉ phải sửa một chỗ và có test khoá lại.
 */
export const POS_STORE_SCOPED_QUERY_KEYS = [
  'pos',
  'pos-browse',
  'pos-promotions',
  'pos-variant-search',
  'pos-variant-serials',
  'pos-warehouse-stocks',
] as const;

function invalidatePosStoreScopedQueries(queryClient: QueryClient): void {
  for (const key of POS_STORE_SCOPED_QUERY_KEYS) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

export interface MyStore {
  storeId: string;
  storeCode: string;
  name: string;
  address?: string;
}

/**
 * TCR-630: logic chọn chi nhánh dùng chung cho header StoreSelector (EG-355 §4)
 * và dropdown "Chi nhánh" trong panel Thông tin đơn hàng POS.
 *
 * Cashier có shift lock — chuyển store = exchangeStoreToken (issue token mới với
 * JWT.sid mới). Owner/manager đa store → chỉ setPosShiftStoreId + invalidate cache.
 * Nguồn "store hiệu lực" duy nhất = resolveActiveStoreId để "store hiển thị"
 * luôn == "store gửi xuống BE" (xem posShift.ts EG-369).
 */
export function useStoreSelection() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((s: RootState) => s.auth.user);

  const { data: stores = [], isLoading, isSuccess } = useQuery<MyStore[]>({
    queryKey: ['auth', 'my-stores'],
    queryFn: () => authService.listMyStores(),
    staleTime: 60_000,
    retry: 0,
  });

  const exchangeMutation = useMutation({
    mutationFn: (storeId: string) => authService.exchangeStoreToken(storeId),
    onSuccess: (tokens, storeId) => {
      if (!user) return;
      dispatch(
        setCredentials({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          user: { ...user, storeId },
        }),
      );
      activateProactiveAccessTokenRefresh(tokens.expires_in);
      // Sync sessionStorage để POS axios interceptor + sidebar dropdown đọc đúng store.
      setPosShiftStoreId(storeId);
      // Invalidate POS queries để re-fetch theo store mới.
      invalidatePosStoreScopedQueries(queryClient);
      const target = stores.find((s) => s.storeId === storeId);
      message.success(`Đã chuyển sang ${target?.name ?? 'chi nhánh'}`);
    },
    onError: () => message.error('Không chuyển được chi nhánh'),
  });

  // Active store source-of-truth: sessionStorage.pos.shiftStoreId (sync với POS
  // sidebar dropdown). Fallback user.storeId nếu sessionStorage trống (cashier
  // chưa mở ca / hook chưa được wire trên page non-POS).
  const shiftStoreId = usePosShiftStoreId();
  // Store hiệu lực = shift ?? user.storeId ?? deliveryStoreId (store mặc định,
  // mirror BE default_store_id) ?? stores[0]. Cùng một nguồn với logic persist
  // bên dưới để "store hiển thị" luôn == "store gửi xuống BE".
  const currentStore = useMemo(() => {
    const activeId = resolveActiveStoreId(
      shiftStoreId,
      stores.map((s) => s.storeId),
      { userStoreId: user?.storeId, deliveryStoreId: user?.deliveryStoreId },
    );
    return stores.find((s) => s.storeId === activeId) ?? stores[0];
  }, [stores, user?.storeId, user?.deliveryStoreId, shiftStoreId]);

  // Đồng bộ "store hiển thị" == "store gửi xuống BE". Owner đa store chưa mở ca
  // có `pos.shiftStoreId` rỗng → header hiển thị store fallback nhưng interceptor
  // không inject `?store_id=` → BE scope theo default_store_id (store khác) →
  // INSTANT tồn = 0. Persist store đang hiển thị để interceptor inject đúng store.
  useEffect(() => {
    // TCR-1694: chỉ hành động khi danh sách chi nhánh đã tải XONG. `stores` mặc
    // định là [] cả lúc đang tải lẫn lúc query lỗi (retry: 0) — xoá binding ở hai
    // trạng thái đó sẽ đá văng chi nhánh hợp lệ của user.
    if (!isSuccess) return;
    const availableStoreIds = stores.map((s) => s.storeId);
    // Binding stale + KHÔNG còn chi nhánh nào truy cập được → xoá hẳn. Không xoá
    // thì interceptor vẫn gắn `?store_id=<cũ>` vào mọi request `/pos/*`.
    if (isPosShiftStoreBindingStale(shiftStoreId, availableStoreIds) && availableStoreIds.length === 0) {
      clearPosShiftStoreId();
      invalidatePosStoreScopedQueries(queryClient);
      return;
    }
    const toBind = resolvePosShiftStoreToPersist(shiftStoreId, availableStoreIds, {
      userStoreId: user?.storeId,
      deliveryStoreId: user?.deliveryStoreId,
    });
    if (!toBind) return;
    setPosShiftStoreId(toBind);
    if (user?.tenantId) setPosShiftStoreTenantId(user.tenantId);
    invalidatePosStoreScopedQueries(queryClient);
  }, [isSuccess, shiftStoreId, stores, user?.storeId, user?.deliveryStoreId, user?.tenantId, queryClient]);

  const switchStore = useCallback(
    (storeId: string) => {
      if (storeId === currentStore?.storeId) return;
      // Cashier (có user.storeId — sid lock from JWT) → exchangeStoreToken
      // để issue token mới với shift_store_id mới. Owner/manager (không sid)
      // → chỉ setPosShiftStoreId + invalidate cache, không cần đổi token.
      if (user?.storeId) {
        exchangeMutation.mutate(storeId);
      } else {
        setPosShiftStoreId(storeId);
        invalidatePosStoreScopedQueries(queryClient);
        const target = stores.find((s) => s.storeId === storeId);
        message.success(`Đã chuyển sang ${target?.name ?? 'chi nhánh'}`);
      }
    },
    [currentStore?.storeId, user?.storeId, exchangeMutation, queryClient, stores],
  );

  return {
    stores,
    isLoading,
    currentStore,
    isSwitching: exchangeMutation.isPending,
    switchStore,
  };
}
